import React, { useRef } from 'react';

import { Input } from '@swyftx/aviary/atoms/Input';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Switch } from '@swyftx/aviary/atoms/Switch';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { EnhancedTabs } from '@swyftx/aviary/molecules/EnhancedTabs';
import { EnhancedTab } from '@swyftx/aviary/molecules/EnhancedTabs/EnhancedTabs.types';
import { MarginOrderDirection, ProtectionOrderVariant } from '@swyftx/margin/build/types';

import { MarginTradeTypeTooltipModal } from '../../TooltipModals';

type Props = {
  title: string;
  value: string;
  leadingLabel: string;
  checked: boolean;
  tabs: EnhancedTab<string>[];
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  tradeSide: MarginOrderDirection;
  protectionOrderType: ProtectionOrderVariant;
  onChangeValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCheckedChange: (checked: boolean) => void;
  onChangeTab: (newValue: string) => void;
};

const MarginProtectionOrderInput: React.FC<Props> = ({
  title,
  onCheckedChange,
  onChangeTab,
  checked,
  tabs,
  placeholder = '0',
  leadingLabel,
  disabled = false,
  tradeSide,
  protectionOrderType,
  onChangeValue,
  value,
  error,
}) => {
  const ref = useRef<HTMLInputElement>(null);

  return (
    <FlexLayout direction='column' spacing={8}>
      <FlexLayout alignItems='center' justifyContent='space-between' className='w-full'>
        <MarginTradeTypeTooltipModal
          defaultValue='closing'
          marginDirection={tradeSide}
          closeType={protectionOrderType === ProtectionOrderVariant.TakeProfit ? 'takeProfit' : 'stopLoss'}
        >
          <Body color='secondary' underline='dashed' className='cursor-pointer'>
            {title}
          </Body>
        </MarginTradeTypeTooltipModal>
        <Switch
          checked={checked}
          disabled={disabled}
          size='sm'
          onCheckedChange={(val) => {
            onCheckedChange(val);
            window.setTimeout(() => ref.current?.focus());
          }}
        />
      </FlexLayout>
      {checked && !disabled && (
        <Input
          variant='monospace'
          ref={ref}
          className='text-right text-16'
          value={value}
          onChange={onChangeValue}
          error={!!error}
          leading={
            <Body className='whitespace-nowrap' color='primary'>
              {leadingLabel}
            </Body>
          }
          placeholder={placeholder}
        />
      )}
      {!!error && (
        <Body size='xsmall' color='error'>
          {error}
        </Body>
      )}
      {checked && <EnhancedTabs fullWidth onChange={onChangeTab} value='' tabs={tabs} size='sm' />}
    </FlexLayout>
  );
};

export { MarginProtectionOrderInput };
